import React, { useEffect, useRef, useState } from 'react'
import SingleAnalyticsHeader from './SingleAnalyticsHeader'
import StudentCardWidget from './StudentCardWidget'
import AnalyticsWidgets from './AnalyticsWidgets'
import { usePageTitle, useQuery } from '../../../utils/util'
import { useTour } from '@reactour/tour'
import { useTranslation } from 'react-i18next'
import { useTourContext } from '../../../context/TourContext'
import DayFilteringWidget from './DayFilteringWidget'
import { daysFilterList } from '../class/ClassAnalyticsWidgets'
import { useAnalyticsUrlFilters } from '../useAnalyticsUrlFilters'

function StudentAnalyticsHome() {
    const query = useQuery()
    const studentId = query.get('id')
    const [student, setStudent] = useState({})
    const { setIsOpen, setSteps, setCurrentStep } = useTour()
    const { t, locale } = useTranslation()

    const [selectedClass, setSelectedClass] = useState(null)

    // This screen has no year/class filter UI of its own: selectedOptions is
    // read straight from the link that opened it.
    const { selectedOptions, daysFilter, setDaysFilter } = useAnalyticsUrlFilters()

    useEffect(() => {
        setSelectedClass(null)
    }, [studentId])

    const [showFilter, setShowFilter] = useState(true)

    const scrollRef = useRef(null)

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        const handleScroll = () => {
            setShowFilter(el.scrollTop > 100)
        }

        el.addEventListener('scroll', handleScroll)

        // initial check
        handleScroll()

        return () => {
            el.removeEventListener('scroll', handleScroll)
        }
    }, [studentId])

    usePageTitle(`Upstrive Education - ${student?.first_name ?? 'Single'} ${student?.last_name ?? 'Student'} Analytics`)

    return (
        <div
            id="studentAnalyticsId"
            className="flex flex-col bg-[#F3EFFF] overflow-y-auto h-screen pr-[10px] xl:pr-[40px] pl-[10px] xl:pl-[40px] transition-all pt-[24px] relative"
            key={studentId}
        >
            <div className="max-w-[1360px] mx-[auto]">
                <SingleAnalyticsHeader />

                <div className="mt-[10px] flex flex-row items-center justify-between">
                    <p className="text-darkenPurple text-[48px] leading-[60px] font-semibold">{t('Student analytics')}</p>
                    <div
                        className="transition-all duration-300 ease-in-out"
                        style={{
                            opacity: showFilter ? 1 : 0,
                            visibility: showFilter ? 'visible' : 'hidden',
                            transform: showFilter ? 'translateY(0)' : 'translateY(-10px)',
                            zIndex: 4,
                        }}
                    >
                        <DayFilteringWidget daysFilter={daysFilter} daysFilterList={daysFilterList} setDaysFilter={setDaysFilter} />
                    </div>
                </div>

                <div className="w-full border-b border-b-white mt-5"></div>
                <div className="flex flex-row  transition-all xl:gap-6 min-h-[745px] " style={{ height: 'max(745px, calc(100vh - 90px))' }}>
                    <div className="pt-[20px]" data-tour="student-card">
                        <StudentCardWidget setStudent={setStudent} student={student} setSelectedClass={setSelectedClass} />
                    </div>
                    <div className="flex overflow-y-auto flex-1 " style={{ minHeight: 'max(745px, 100%)' }} data-tour="analytics-widgets">
                        <AnalyticsWidgets
                            student={student}
                            subjectId={studentId}
                            ref={scrollRef}
                            selectedOptions={selectedOptions}
                            daysFilter={daysFilter}
                            setDaysFilter={setDaysFilter}
                            selectedClass={selectedClass}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StudentAnalyticsHome
